/**
 * TypeScript types for Convex discriminated unions
 * These types mirror the schema definitions and provide type safety
 */

import { Infer, v } from 'convex/values'
import type { Doc } from '../_generated/dataModel'
import { playerAction, playerActionArg } from './validators/playerAction'
import { playerSnapshot, playerTimelines } from './validators/playerSnapshot'
import type { Pot, PotStructure, PotDistribution, PotWinner, Card } from '@ppwr/poker-engine'
import { HandRank } from '../../../poker-engine/src/types'

// Re-export pot types from poker-engine for use in Convex layer
export type { Pot, PotStructure, PotDistribution, PotWinner }

/**
 * API argument type for player actions
 * Mirrors validators.playerActionArg from schema
 */
export type PlayerActionArg = Infer<typeof playerActionArg>

/**
 * Player action discriminated union
 * Extracted from schema to use in code with full type safety
 *
 * Key feature: call, fold, check, call and allin  don't have amount field,
 * while bet and raise require it
 */
export type PlayerAction = Infer<typeof playerAction>

/**
 * Action type (discriminator value only)
 */
export type ActionType = PlayerAction['action']

/**
 * Helper type to check if an action requires an amount
 */
export type PlayerActionWithAmount = Extract<PlayerAction, { amount: number }>
export type PlayerActionWithoutAmount = Exclude<PlayerAction, ActionWithAmount>

export type ActionWithAmount = Extract<ActionType, { amount: number }>
export type ActionWithoutAmount = Exclude<ActionType, ActionWithAmount>

// Type guards for runtime checking
export function isPlayerActionWithAmount(playerAction: PlayerAction): playerAction is PlayerActionWithAmount {
  return (
    playerAction.action === 'call' ||
    playerAction.action === 'bet' ||
    playerAction.action === 'raise' ||
    playerAction.action === 'allin' ||
    playerAction.action === 'blind'
  )
}

export function isPlayerActionWithoutAmount(playerAction: PlayerAction): playerAction is PlayerActionWithoutAmount {
  return playerAction.action === 'fold' || playerAction.action === 'check'
}

// Type guards for runtime checking
export function isActionWithAmount(action: ActionType): action is ActionWithAmount {
  return action === 'call' || action === 'bet' || action === 'raise' || action === 'allin'
}

export function isActionWithoutAmount(action: ActionType): action is ActionWithoutAmount {
  return action === 'fold' || action === 'check'
}

/**
 * Game state discriminated union types
 * Extracted from schema for type safety
 */
export type PregameGame = Extract<Doc<'games'>, { state: 'pregame' }>
export type PlayingGame = Extract<Doc<'games'>, { state: 'playing' }>
export type FinishedGame = Extract<Doc<'games'>, { state: 'finished' }>

export type Game = PregameGame | PlayingGame | FinishedGame

/**
 * Extract boardState type for playing games
 * Inferred from schema-derived PlayingGame type (single source of truth)
 */
export type PlayingBoardState = PlayingGame['boardState']

/**
 * Sanitized game types (without deck or playerTimelines - only used by backend)
 */
export type SanitizedPregameGame = Omit<PregameGame, 'deck' | 'playerTimelines'>
export type SanitizedPlayingGame = Omit<PlayingGame, 'deck' | 'playerTimelines'>
export type SanitizedFinishedGame = Omit<FinishedGame, 'deck' | 'playerTimelines'>

export type SanitizedGame = SanitizedPregameGame | SanitizedPlayingGame | SanitizedFinishedGame

/**
 * Type guards for game states (backend - includes deck)
 */
export function isPregameGame(game: Doc<'games'>): game is PregameGame {
  return game.state === 'pregame'
}

export function isPlayingGame(game: Doc<'games'>): game is PlayingGame {
  return game.state === 'playing'
}

export function isFinishedGame(game: Doc<'games'>): game is FinishedGame {
  return game.state === 'finished'
}

/**
 * Type guards for sanitized games (frontend - no deck)
 * Use these in frontend code when working with sanitized game data
 */
export function isSanitizedPregameGame(game: SanitizedGame): game is SanitizedPregameGame {
  return game.state === 'pregame'
}

export function isSanitizedPlayingGame(game: SanitizedGame): game is SanitizedPlayingGame {
  return game.state === 'playing'
}

export function isSanitizedFinishedGame(game: SanitizedGame): game is SanitizedFinishedGame {
  return game.state === 'finished'
}

export const handRanks = v.union(
  v.literal('Straight Flush'),
  v.literal('Four of a Kind'),
  v.literal('Full House'),
  v.literal('Flush'),
  v.literal('Straight'),
  v.literal('Three of a Kind'),
  v.literal('Two Pair'),
  v.literal('Pair'),
  v.literal('High Card'),
  v.literal('Win by fold')
)
export type HandRanks = Infer<typeof handRanks>

export type Winner = {
  playerId: string
  userId: string
  playerName: string
  amount: number
  cards: Card[]
  handRank: HandRanks
  handDescription: string
}

/**
 * Player snapshot for hand history
 * Inferred from playerSnapshot validator (single source of truth)
 */
export type PlayerSnapshot = Infer<typeof playerSnapshot>

/**
 * Player timelines for hand history
 * Inferred from playerTimelines validator (single source of truth)
 */
export type PlayerTimelines = Infer<typeof playerTimelines>

/**
 * Type representing a sanitized player with potentially hidden cards and last action
 */
export type SanitizedPlayer = Omit<Doc<'players'>, 'cards'> & {
  cards: string[]
  lastAction?: Doc<'games'>['currentHandActions'][number]
}
